// AdminClients.js — Listagem e CRUD de Clientes
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Building2,
  Search,
  History,
  Loader2,
  Save,
  X,
  RotateCcw,
  Power,
  Copy,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", sector: "", payment_terms: 30, billable_months: 11, notes: "", active: true };

const SECTOR_SUGGESTIONS = [
  "Tecnologia",
  "Banca / Finanças",
  "Seguros",
  "Saúde",
  "Indústria",
  "Consultoria",
  "Retalho / E-commerce",
  "Telecomunicações",
  "Energia / Utilities",
  "Transportes / Logística",
  "Educação",
  "Setor Público",
  "Outro",
];

const AdminClients = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // null | {id?, ...}
  const [form, setForm] = useState(emptyForm);
  const [sectorMode, setSectorMode] = useState("select"); // select | custom
  const [statusFilter, setStatusFilter] = useState("active"); // active | inactive | all
  const [sectorFilter, setSectorFilter] = useState("__all__");
  const [existingSectors, setExistingSectors] = useState([]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/clients`, { headers: getAuthHeaders() });
      setClients(r.data);
    } catch (e) {
      console.error("[AdminClients] fetchAll:", e);
      toast.error("Erro a carregar clientes");
    } finally {
      setLoading(false);
    }
  };

  const fetchSectors = async () => {
    try {
      const r = await axios.get(`${API}/clients/sectors`, { headers: getAuthHeaders() });
      setExistingSectors(r.data || []);
    } catch (e) {
      console.error("[AdminClients] fetchSectors:", e);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchSectors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setEditing({});
    setForm(emptyForm);
    setSectorMode("select");
  };
  const openEdit = (c) => {
    setEditing(c);
    const sect = c.sector || "";
    setForm({
      name: c.name || "",
      sector: sect,
      payment_terms: c.payment_terms ?? 30,
      billable_months: c.billable_months ?? 11,
      notes: c.notes || "",
      active: c.active !== false,
    });
    // se o setor não está nas sugestões nem em existing, modo custom
    const inSuggestions = SECTOR_SUGGESTIONS.includes(sect);
    setSectorMode(sect && !inSuggestions ? "custom" : "select");
  };
  const close = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    const payload = { ...form };
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.put(`${API}/clients/${editing.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Cliente atualizado");
      } else {
        await axios.post(`${API}/clients`, payload, { headers: getAuthHeaders() });
        toast.success("Cliente criado");
      }
      close();
      await fetchAll();
      fetchSectors();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  const reactivate = async (c) => {
    try {
      await axios.put(`${API}/clients/${c.id}`, { active: true }, { headers: getAuthHeaders() });
      toast.success("Cliente reativado");
      await fetchAll();
    } catch {
      toast.error("Erro ao reativar");
    }
  };

  const [duplicating, setDuplicating] = useState(null); // {src, name, payment_terms, billable_months}
  const startDuplicate = (c) => {
    setDuplicating({
      src: c,
      name: `${c.name} - Prazo ${c.payment_terms}d`,
      payment_terms: c.payment_terms,
      billable_months: c.billable_months,
    });
  };
  const confirmDuplicate = async () => {
    if (!duplicating) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/clients/${duplicating.src.id}/duplicate`,
        {
          name: duplicating.name,
          payment_terms: duplicating.payment_terms,
          billable_months: duplicating.billable_months,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Cliente duplicado");
      setDuplicating(null);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao duplicar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Desativar cliente "${c.name}"?`)) return;
    try {
      await axios.delete(`${API}/clients/${c.id}`, { headers: getAuthHeaders() });
      toast.success("Cliente desativado");
      await fetchAll();
    } catch {
      toast.error("Erro ao desativar");
    }
  };

  const allSectors = Array.from(new Set([...SECTOR_SUGGESTIONS, ...existingSectors])).filter((s) => s && s !== "Outro");

  const filtered = clients.filter((c) => {
    if (statusFilter === "active" && c.active === false) return false;
    if (statusFilter === "inactive" && c.active !== false) return false;
    if (sectorFilter === "__none__" && (c.sector || "") !== "") return false;
    if (sectorFilter !== "__all__" && sectorFilter !== "__none__" && (c.sector || "") !== sectorFilter) return false;
    if (query.trim() && !(c.name || "").toLowerCase().includes(query.trim().toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50 font-sans" data-testid="admin-clients-page">
      <div className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin">
              <Button variant="outline" size="sm" className="border-slate-300" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-brand-600 flex items-center gap-2">
                <Building2 className="w-6 h-6" />
                Clientes
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Padronize <strong>prazo de pagamento</strong> e <strong>meses faturáveis</strong> por
                cliente. Estes valores são pré-aplicados no simulador (e continuam editáveis).
              </p>
            </div>
          </div>
          <Button
            onClick={openNew}
            size="sm"
            className="bg-brand-600 hover:bg-brand-700 text-white"
            data-testid="new-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo cliente
          </Button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-4">
          <div className="relative md:col-span-5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Procurar por nome..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 bg-white"
              data-testid="search-input"
            />
          </div>
          <div className="md:col-span-4">
            <Select value={sectorFilter} onValueChange={setSectorFilter}>
              <SelectTrigger className="bg-white" data-testid="sector-filter">
                <SelectValue placeholder="Filtrar por setor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os setores</SelectItem>
                {allSectors.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
                <SelectItem value="__none__">— sem setor —</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-white" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Apenas ativos</SelectItem>
                <SelectItem value="inactive">Apenas inativos</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="p-10 text-center text-slate-500">
              <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Sem clientes registados.</p>
              <p className="text-xs mt-1">
                Clique em <strong>Novo cliente</strong> para adicionar o primeiro.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="clients-list">
            {filtered.map((c) => (
              <Card
                key={c.id}
                className={`bg-white border ${c.active ? "border-slate-200" : "border-slate-200 opacity-60"}`}
                data-testid={`client-card-${c.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-brand-600 truncate">{c.name}</h3>
                        {c.sector && (
                          <span className="text-[10px] bg-brand-50 text-brand-700 border border-brand-200 rounded px-1.5 py-0.5 font-medium" data-testid={`sector-badge-${c.id}`}>
                            {c.sector}
                          </span>
                        )}
                        {!c.active && (
                          <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">
                            inativo
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-slate-600">
                        <span>
                          Prazo:{" "}
                          <strong className="text-slate-800 font-mono">
                            {c.payment_terms} d
                          </strong>
                        </span>
                        <span>
                          Meses faturáveis:{" "}
                          <strong className="text-slate-800 font-mono">
                            {c.billable_months}
                          </strong>
                        </span>
                      </div>
                      {c.notes && (
                        <p className="text-[11px] text-slate-500 mt-2 line-clamp-2">{c.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => navigate(`/admin/clients/${c.id}`)}
                        className="h-8 w-8 text-brand-600 hover:bg-brand-50"
                        title="Ver histórico"
                        data-testid={`history-${c.id}`}
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEdit(c)}
                        className="h-8 w-8"
                        title="Editar"
                        data-testid={`edit-${c.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => startDuplicate(c)}
                        className="h-8 w-8 text-brand-600 hover:bg-brand-50"
                        title="Duplicar (ex.: outro prazo)"
                        data-testid={`duplicate-${c.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      {c.active ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => remove(c)}
                          className="h-8 w-8 text-red-500 hover:bg-red-50"
                          title="Desativar"
                          data-testid={`delete-${c.id}`}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => reactivate(c)}
                          className="h-8 w-8 text-emerald-600 hover:bg-emerald-50"
                          title="Reativar"
                          data-testid={`reactivate-${c.id}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dialog */}
        <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-brand-600">
                {editing?.id ? "Editar Cliente" : "Novo Cliente"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Nome</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex.: ACME Lda"
                  data-testid="client-name-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Setor de atividade</Label>
                {sectorMode === "select" ? (
                  <Select
                    value={form.sector || "__none__"}
                    onValueChange={(v) => {
                      if (v === "Outro") {
                        setSectorMode("custom");
                        setForm({ ...form, sector: "" });
                      } else if (v === "__none__") {
                        setForm({ ...form, sector: "" });
                      } else {
                        setForm({ ...form, sector: v });
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1" data-testid="client-sector-select">
                      <SelectValue placeholder="Selecione um setor..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sem setor —</SelectItem>
                      {SECTOR_SUGGESTIONS.filter((s) => s !== "Outro").map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                      {existingSectors
                        .filter((s) => !SECTOR_SUGGESTIONS.includes(s))
                        .map((s) => (
                          <SelectItem key={`existing-${s}`} value={s}>{s}</SelectItem>
                        ))}
                      <SelectItem value="Outro">Outro… (escrever)</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={form.sector}
                      onChange={(e) => setForm({ ...form, sector: e.target.value })}
                      placeholder="Ex.: Marketing Digital"
                      data-testid="client-sector-custom"
                      autoFocus
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSectorMode("select")}
                      className="flex-shrink-0"
                    >
                      ← Lista
                    </Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Prazo Pagamento (dias)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.payment_terms}
                    onChange={(e) =>
                      setForm({ ...form, payment_terms: parseInt(e.target.value) || 0 })
                    }
                    data-testid="client-payment-terms-input"
                    className="mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs">Meses Faturáveis</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max="12"
                    step="0.25"
                    value={form.billable_months}
                    onChange={(e) => {
                      const raw = (e.target.value || "").toString().replace(",", ".");
                      if (raw === "") {
                        setForm({ ...form, billable_months: "" });
                        return;
                      }
                      const v = parseFloat(raw);
                      setForm({ ...form, billable_months: Number.isFinite(v) ? v : "" });
                    }}
                    data-testid="client-billable-months-input"
                    className="mt-1 font-mono"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Aceita decimais (ex.: 10.5 ou 10,75)</p>
                </div>
              </div>
              <div>
                <Label className="text-xs">Notas</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Observações internas..."
                  className="mt-1 min-h-[60px]"
                  data-testid="client-notes-input"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <div className="flex flex-col">
                  <Label className="text-xs">Cliente ativo</Label>
                  <span className="text-[10px] text-slate-500">Cliente inativo deixa de aparecer no selector do simulador.</span>
                </div>
                <Switch
                  checked={form.active !== false}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="client-active-toggle"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={saving}>
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-brand-600 hover:bg-brand-700 text-white"
                data-testid="save-client-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Duplicate dialog */}
        <Dialog open={duplicating !== null} onOpenChange={(o) => !o && setDuplicating(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-brand-600">
                Duplicar Cliente
              </DialogTitle>
            </DialogHeader>
            {duplicating && (
              <div className="space-y-3 py-2">
                <p className="text-[11px] text-slate-500">
                  Cria uma variante de <strong>{duplicating.src.name}</strong>. Útil para
                  ter o mesmo cliente com prazos de pagamento diferentes.
                </p>
                <div>
                  <Label className="text-xs">Nome da variante</Label>
                  <Input
                    value={duplicating.name}
                    onChange={(e) => setDuplicating({ ...duplicating, name: e.target.value })}
                    className="mt-1"
                    data-testid="duplicate-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Prazo Pagamento (dias)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={duplicating.payment_terms}
                      onChange={(e) =>
                        setDuplicating({
                          ...duplicating,
                          payment_terms: parseInt(e.target.value) || 0,
                        })
                      }
                      className="mt-1 font-mono"
                      data-testid="duplicate-payment-terms-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Meses Faturáveis</Label>
                    <Input
                      type="number"
                      step="0.25"
                      min="0"
                      max="12"
                      value={duplicating.billable_months}
                      onChange={(e) => {
                        const raw = (e.target.value || "").toString().replace(",", ".");
                        const v = parseFloat(raw);
                        setDuplicating({
                          ...duplicating,
                          billable_months: Number.isFinite(v) ? v : "",
                        });
                      }}
                      className="mt-1 font-mono"
                      data-testid="duplicate-billable-months-input"
                    />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDuplicating(null)} disabled={saving}>
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
              <Button
                onClick={confirmDuplicate}
                disabled={saving}
                className="bg-brand-600 hover:bg-brand-700 text-white"
                data-testid="confirm-duplicate-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Copy className="w-4 h-4 mr-2" />
                )}
                Duplicar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminClients;
